import React, { useLayoutEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Avatar,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateRangeIcon,
  Group as GroupIcon,
  Timeline as TimelineIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { getPayload, setPutPayloadWithParams } from "../services/api";
import LoadingBackDrop from "../scenes/global/LoadingBackDrop";
import dayjs from "dayjs";
import CubesToBeCasted from "../scenes/dashboardCharts/CubesToBeCasted";
import PDFViewerDialog from "../scenes/jasperReports/PDFViewerDialog";

// Styled components
const SummaryCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
  },
}));

const ChartPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
}));

const COLORS = [
  "#2196f3",
  "#ff9800",
  "#4caf50",
  "#9c27b0",
  "#e91e63",
  "#00bcd4",
  "#795548",
  "#607d8b",
];

const DashboardProgressNew = ({ loggedUser }) => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    pendingJobs: 0,
    completedJobs: 0,
    dailyIncome: 0,
    monthlyIncome: 0,
    income: 0,
    listIncomeOnly: [],
    listExpenseOnly: [],
    userAssignedJobs: { body: [] },
    allLabRequestTests: [],
  });

  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [openLabSheetDialog, setOpenLabSheetDialog] = useState(false);

  const handleCloseLabsheet = () => {
    setOpenLabSheetDialog(false);
  };
  // Generate years for selection (last 5 years and next 2 years)
  const years = Array.from(
    { length: 8 },
    (_, i) => new Date().getFullYear() - 5 + i,
  );

  useLayoutEffect(() => {
    loadDashboardData(currentYear);
  }, [currentYear]);
  const [testingDate, settestingDate] = useState(
    dayjs(new Date()).tz("Asia/Colombo", true),
  );
  const loadDashboardData = async (year) => {
    setLoading(true);

    try {
      console.log("came");
      const response = await getPayload.getPayloadData(
        `/dashboard/data?userId=${loggedUser.id}&testDate=${testingDate.format(
          "YYYY-MM-DD",
        )}&year=${year}`,
      );

      console.log("Dashboard Data:", response.data);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "LKR 0.00";
    return `LKR ${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format compact currency for charts
  const formatCompactCurrency = (value) => {
    if (value >= 1000000) {
      return `LKR ${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `LKR ${(value / 1000).toFixed(0)}K`;
    }
    return `LKR ${value}`;
  };

  // Process monthly income/expenses data
  const processMonthlyData = () => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const incomeList = dashboardData.listIncomeOnly || [];
    const expenseList = dashboardData.listExpenseOnly || [];

    return months
      .slice(0, Math.max(incomeList.length, expenseList.length))
      .map((month, index) => ({
        month,
        income: incomeList[index] || 0,
        expenses: expenseList[index] || 0,
        profit: (incomeList[index] || 0) - (expenseList[index] || 0),
      }));
  };

  // Process TO performance data from allLabRequestTests
  const processTOPerformance = () => {
    const testsData = dashboardData.allLabRequestTests || [];

    return testsData.map((item, index) => ({
      id: index,
      name: item[0],
      total: item[1],
      completed: item[2],
      pending: item[3],
      completionRate: item[1] > 0 ? ((item[2] / item[1]) * 100).toFixed(1) : 0,
    }));
  };

  // Process user assigned jobs for alternative view
  const processUserAssignedJobs = () => {
    const body = dashboardData.userAssignedJobs?.body || [];
    return body.map((item) => ({
      id: item.id,
      name: item.label,
      value: item.value,
    }));
  };

  // Calculate total income and expenses
  const totalIncome = (dashboardData.listIncomeOnly || []).reduce(
    (sum, val) => sum + val,
    0,
  );
  const totalExpenses = (dashboardData.listExpenseOnly || []).reduce(
    (sum, val) => sum + val,
    0,
  );
  const netProfit = totalIncome - totalExpenses;

  // Mock percentage changes (replace with actual calculations)
  const percentageChanges = {
    pending: -8.5,
    completed: 12.3,
    daily: 5.7,
    annual: 15.8,
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ py: 4, bgcolor: "#f8f9fc", minHeight: "100vh" }}
    >
      {loading && <LoadingBackDrop openBackDrop={loading} />}

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#1a237e", mb: 1 }}
            >
              Lab Dashboard
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Welcome back, {loggedUser?.firstName}! Here's your laboratory
              performance overview.
            </Typography>
          </Box>

          {/* Year Selector */}
          <FormControl sx={{ minWidth: 140 }} size="medium">
            <InputLabel>Select Year</InputLabel>
            <Select
              value={currentYear}
              onChange={(e) => setCurrentYear(e.target.value)}
              label="Select Year"
              sx={{
                borderRadius: 2,
                bgcolor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                minWidth: 140,
              }}
              startAdornment={
                <CalendarIcon sx={{ mr: 1, color: "primary.main" }} />
              }
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Pending Jobs */}
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              elevation={0}
              sx={{
                bgcolor: "#fff",
                borderLeft: "4px solid #ff9800",
                position: "relative",
                overflow: "visible",
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight={600}
                      letterSpacing={0.5}
                    >
                      PENDING JOBS
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 700, color: "#ff9800", my: 1 }}
                    >
                      {dashboardData.pendingJobs}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TrendingDownIcon
                        sx={{ color: "success.main", fontSize: 16 }}
                      />
                      <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight={600}
                      >
                        {percentageChanges.pending}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        vs last month
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar sx={{ bgcolor: "#fff3e0", width: 56, height: 56 }}>
                    <PendingIcon sx={{ color: "#ff9800", fontSize: 30 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </SummaryCard>
          </Grid>

          {/* Completed Jobs */}
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              elevation={0}
              sx={{
                bgcolor: "#fff",
                borderLeft: "4px solid #4caf50",
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight={600}
                      letterSpacing={0.5}
                    >
                      COMPLETED JOBS
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 700, color: "#4caf50", my: 1 }}
                    >
                      {dashboardData.completedJobs}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TrendingUpIcon
                        sx={{ color: "success.main", fontSize: 16 }}
                      />
                      <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight={600}
                      >
                        +{percentageChanges.completed}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        vs last month
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar sx={{ bgcolor: "#e8f5e9", width: 56, height: 56 }}>
                    <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 30 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </SummaryCard>
          </Grid>

          {/* Daily Income */}
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              elevation={0}
              sx={{
                bgcolor: "#fff",
                borderLeft: "4px solid #2196f3",
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight={600}
                      letterSpacing={0.5}
                    >
                      DAILY INCOME
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#2196f3", my: 1 }}
                    >
                      {formatCompactCurrency(dashboardData.dailyIncome)}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TrendingUpIcon
                        sx={{ color: "info.main", fontSize: 16 }}
                      />
                      <Typography
                        variant="caption"
                        color="info.main"
                        fontWeight={600}
                      >
                        +{percentageChanges.daily}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        vs yesterday
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar sx={{ bgcolor: "#e3f2fd", width: 56, height: 56 }}>
                    <MoneyIcon sx={{ color: "#2196f3", fontSize: 30 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </SummaryCard>
          </Grid>

          {/* Annual Income */}
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              elevation={0}
              sx={{
                bgcolor: "#fff",
                borderLeft: "4px solid #9c27b0",
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight={600}
                      letterSpacing={0.5}
                    >
                      ANNUAL INCOME
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#9c27b0", my: 1 }}
                    >
                      {formatCompactCurrency(dashboardData.income)}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TrendingUpIcon
                        sx={{ color: "secondary.main", fontSize: 16 }}
                      />
                      <Typography
                        variant="caption"
                        color="secondary"
                        fontWeight={600}
                      >
                        +{percentageChanges.annual}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        vs last year
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar sx={{ bgcolor: "#f3e5f5", width: 56, height: 56 }}>
                    <AccountBalanceIcon
                      sx={{ color: "#9c27b0", fontSize: 30 }}
                    />
                  </Avatar>
                </Box>
              </CardContent>
            </SummaryCard>
          </Grid>
        </Grid>

        {/* Income & Expenses Chart */}
        <ChartPaper elevation={0} sx={{ mb: 4 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <TimelineIcon color="primary" />
                Income & Expenses Overview - {currentYear}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Monthly breakdown of income, expenses, and net profit
              </Typography>
            </Box>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                label={`Total Income: ${formatCompactCurrency(totalIncome)}`}
                color="primary"
                variant="outlined"
                size="small"
                icon={<TrendingUpIcon />}
              />
              <Chip
                label={`Total Expenses: ${formatCompactCurrency(totalExpenses)}`}
                color="error"
                variant="outlined"
                size="small"
                icon={<TrendingDownIcon />}
              />
              <Chip
                label={`Net Profit: ${formatCompactCurrency(netProfit)}`}
                color={netProfit >= 0 ? "success" : "error"}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>

          <ResponsiveContainer width="100%" height={450}>
            <AreaChart data={processMonthlyData()}>
              <defs>
                <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2196f3" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#2196f3" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="expensesGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#f44336" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f44336" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4caf50" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis
                tickFormatter={(value) => `LKR ${(value / 1000).toFixed(0)}K`}
                tick={{ fill: "#666", fontSize: 12 }}
              />
              <RechartsTooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  borderRadius: 8,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  border: "none",
                }}
              />
              <Legend wrapperStyle={{ paddingTop: 20 }} />
              <Area
                type="monotone"
                dataKey="income"
                stroke="#2196f3"
                strokeWidth={2}
                fill="url(#incomeGradient)"
                name="Income"
                dot={{ r: 3, fill: "#2196f3" }}
                activeDot={{ r: 6, fill: "#2196f3" }}
              />
              <Area
                type="monotone"
                dataKey="expenses"
                stroke="#f44336"
                strokeWidth={2}
                fill="url(#expensesGradient)"
                name="Expenses"
                dot={{ r: 3, fill: "#f44336" }}
                activeDot={{ r: 6, fill: "#f44336" }}
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="#4caf50"
                strokeWidth={3}
                name="Net Profit"
                dot={{ r: 4, fill: "#4caf50", strokeWidth: 2 }}
                activeDot={{ r: 8, fill: "#4caf50" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartPaper>

        {/* Two Charts Side by Side */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* TO Performance Bar Chart */}
          <Grid item xs={12} md={7}>
            <ChartPaper elevation={0}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <GroupIcon color="primary" />
                    Technical Officer Performance
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Job distribution - Pending vs Completed by each TO
                  </Typography>
                </Box>
                <Box display="flex" gap={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        bgcolor: "#ff9800",
                        borderRadius: 1,
                      }}
                    />
                    <Typography variant="caption">Pending</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        bgcolor: "#4caf50",
                        borderRadius: 1,
                      }}
                    />
                    <Typography variant="caption">Completed</Typography>
                  </Box>
                </Box>
              </Box>

              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={processTOPerformance()}
                  barGap={0}
                  barCategoryGap="20%"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#666", fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fill: "#666", fontSize: 12 }} />
                  <RechartsTooltip
                    contentStyle={{
                      borderRadius: 8,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                      border: "none",
                    }}
                    cursor={{ fill: "rgba(0,0,0,0.05)" }}
                  />
                  <Legend wrapperStyle={{ paddingTop: 20 }} />
                  <Bar
                    dataKey="completed"
                    fill="#4caf50"
                    name="Completed Jobs"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={60}
                  />
                  <Bar
                    dataKey="pending"
                    fill="#ff9800"
                    name="Pending Jobs"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={60}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartPaper>
          </Grid>

          {/* Test Category Distribution */}
          <Grid item xs={12} md={5}>
            <ChartPaper elevation={0}>
              <Box mb={3}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <TimelineIcon color="primary" />
                  Job Distribution by TO
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total assigned jobs per Technical Officer
                </Typography>
              </Box>

              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={processUserAssignedJobs()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) =>
                      `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                    }
                    outerRadius={130}
                    innerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {processUserAssignedJobs().map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value) => [`${value} jobs`, "Total"]}
                    contentStyle={{
                      borderRadius: 8,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                      border: "none",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Legend */}
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="center"
                gap={2}
                mt={2}
              >
                {processUserAssignedJobs().map((item, index) => (
                  <Chip
                    key={item.id}
                    label={`${item.name}: ${item.value}`}
                    size="small"
                    sx={{
                      bgcolor: COLORS[index % COLORS.length],
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </ChartPaper>
          </Grid>
        </Grid>

        {/* Detailed TO Performance Cards */}
        <ChartPaper elevation={0}>
          <Box mb={3}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <AssignmentIcon color="primary" />
              Detailed TO Performance Breakdown
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Completion rates and job distribution details
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {processTOPerformance().map((to, index) => (
              <Grid item xs={12} sm={6} md={3} key={to.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: "#fafbfe",
                    border: "1px solid #e0e0e0",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      transform: "translateY(-2px)",
                      borderColor: COLORS[index % COLORS.length],
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar
                      sx={{
                        bgcolor: COLORS[index % COLORS.length],
                        width: 45,
                        height: 45,
                      }}
                    >
                      {to.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {to.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Technical Officer
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        Total Jobs
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {to.total}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        Completed
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        color="success.main"
                      >
                        {to.completed}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        Pending
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        color="warning.main"
                      >
                        {to.pending}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" fontWeight={500}>
                        Completion Rate
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="primary"
                      >
                        {to.completionRate}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(to.completionRate)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": {
                          bgcolor:
                            to.completionRate >= 80
                              ? "#4caf50"
                              : to.completionRate >= 50
                                ? "#ff9800"
                                : "#f44336",
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </ChartPaper>
        {dashboardData.allCastingCubes &&
          dashboardData.allCastingCubes.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <CubesToBeCasted
                castingCubesData={dashboardData.allCastingCubes}
                setSelectedRequestId={setSelectedRequestId}
                setOpenLabSheetDialog={setOpenLabSheetDialog}
              />
            </Box>
          )}
      </Box>

      <PDFViewerDialog
        reportNo={`CUBE_TEST_${selectedRequestId?.id}_${
          selectedRequestId?.labRequest?.year
        }_${String(selectedRequestId?.id).padStart(4, "0")}`}
        labRequestId={selectedRequestId?.id}
        open={openLabSheetDialog}
        handleCloseReportPreview={handleCloseLabsheet}
        letterHead={""}
        jobId={selectedRequestId?.id} //this is selected test id
        reportType={"LABSHEET"}
      />
    </Container>
  );
};

export default DashboardProgressNew;
